import type { TFunction } from 'i18next';
import type { AssessmentResultItemDataFragment } from '../../../Trainee/TraineeReports.api';
import type { CognitiveFitness, SimpleMetric } from '../../model';
import type { AitResult } from '@/apolloClient.types';
import { formatDecimalValue } from '@/utils/common';

const parseAit = (t: TFunction, items: AssessmentResultItemDataFragment[]): CognitiveFitness | null => {
    const item = items.find(i => i.result.__typename === 'AitResult');
    const mi = item?.moduleInstance;
    const r = item?.result as AitResult;
    if (!mi?.name || !r) {
        return null;
    }

    const metrics: SimpleMetric[] = [];

    // Composite Score
    metrics.push({
        label: t('traineeReports:performance.cognitiveFitness.score'),
        value: Math.round(r.compositeScore ?? 0),
    });

    // Normalised Accuracy
    metrics.push({
        label: t('traineeReports:performance.cognitiveFitness.accuracy'),
        value: formatDecimalValue(r.normalisedAccuracy ?? 0),
    });

    // Normalised Flexibility
    metrics.push({
        label: t('traineeReports:performance.cognitiveFitness.flexibility'),
        value: formatDecimalValue(r.normalisedFlexibility ?? 0),
    });

    // Normalised Speed (Adaptive Speed)
    metrics.push({
        label: t('traineeReports:performance.cognitiveFitness.adaptiveSpeed'),
        value: formatDecimalValue(r.normalisedSpeed ?? 0),
    });

    // Agility Level
    if (r.levelDetail) {
        const levelLabel = t('traineeReports:performance.cognitiveFitness.agilityLevelValue', {
            level: r.levelDetail.level,
            description: r.levelDetail.label,
        });
        metrics.push({
            label: t('traineeReports:performance.cognitiveFitness.agilityLevel'),
            value: levelLabel,
        });
    }

    return {
        name: mi.name ?? '',
        moduleDefinitionIdentifier: mi.moduleDefinitionIdentifier ?? '',
        description: mi.description ?? '',
        result: metrics,
    };
};

export default parseAit;
